import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Receipt,
  Camera,
  Check,
  X,
  Loader2,
  ImageIcon,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const EXPENSE_CATEGORIES = {
  cleaning_supplies: { ru: 'Химия / расходники', en: 'Cleaning supplies' },
  equipment: { ru: 'Оборудование', en: 'Equipment' },
  fuel: { ru: 'Бензин', en: 'Fuel' },
  travel: { ru: 'Поездка', en: 'Travel' },
  office: { ru: 'Офис', en: 'Office' },
  advertising: { ru: 'Реклама', en: 'Advertising' },
  insurance: { ru: 'Страховка', en: 'Insurance' },
  rent: { ru: 'Аренда', en: 'Rent' },
  utilities: { ru: 'Коммунальные', en: 'Utilities' },
  other: { ru: 'Другое', en: 'Other' },
};

const STATUS_BADGES = {
  pending: { label: { ru: 'Ожидает', en: 'Pending' }, variant: 'outline' },
  approved: { label: { ru: 'Одобрен', en: 'Approved' }, className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  rejected: { label: { ru: 'Отклонён', en: 'Rejected' }, className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  synced: { label: { ru: 'В QB', en: 'Synced' }, className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
};

export function OrderExpenses({ orderId, canManage }) {
  const { api, user } = useAuth();
  const { lang } = useLanguage();
  const [expenses, setExpenses] = useState([]);
  const [totalApproved, setTotalApproved] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const fetchExpenses = useCallback(async () => {
    try {
      const res = await api.get(`/expenses/order/${orderId}`);
      setExpenses(res.data.items || []);
      setTotalApproved(res.data.total_approved || 0);
    } catch {
      // Silently fail — expenses are optional
    } finally {
      setLoading(false);
    }
  }, [api, orderId]);

  useEffect(() => {
    if (orderId) fetchExpenses();
  }, [orderId, fetchExpenses]);

  const handleApprove = async (expenseId) => {
    try {
      await api.post(`/expenses/${expenseId}/approve`);
      toast.success(lang === 'ru' ? 'Расход одобрен' : 'Expense approved');
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleReject = async (expenseId) => {
    try {
      await api.post(`/expenses/${expenseId}/reject`);
      toast.success(lang === 'ru' ? 'Расход отклонён' : 'Expense rejected');
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (expenseId) => {
    try {
      await api.delete(`/expenses/${expenseId}`);
      toast.success(lang === 'ru' ? 'Расход удалён' : 'Expense deleted');
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4 text-muted-foreground text-sm">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        {lang === 'ru' ? 'Загрузка расходов...' : 'Loading expenses...'}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Receipt className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            {lang === 'ru' ? 'Расходы' : 'Expenses'}
          </span>
          {totalApproved > 0 && (
            <Badge variant="secondary" className="text-xs">
              ${totalApproved.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </Badge>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={() => setShowAddDialog(true)}
        >
          <Plus className="h-3 w-3 mr-1" />
          {lang === 'ru' ? 'Добавить' : 'Add'}
        </Button>
      </div>

      {/* Expense List */}
      {expenses.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-2">
          {lang === 'ru' ? 'Нет расходов' : 'No expenses'}
        </p>
      ) : (
        <div className="space-y-2">
          {expenses.map((exp) => (
            <ExpenseRow
              key={exp.id}
              expense={exp}
              lang={lang}
              canManage={canManage}
              isOwner={exp.submitted_by === user?.id}
              onApprove={() => handleApprove(exp.id)}
              onReject={() => handleReject(exp.id)}
              onDelete={() => handleDelete(exp.id)}
              api={api}
            />
          ))}
        </div>
      )}

      {/* Add Expense Dialog */}
      <AddExpenseDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        orderId={orderId}
        api={api}
        lang={lang}
        onSuccess={fetchExpenses}
      />
    </div>
  );
}

function ExpenseRow({ expense, lang, canManage, isOwner, onApprove, onReject, onDelete, api }) {
  const cat = EXPENSE_CATEGORIES[expense.category] || EXPENSE_CATEGORIES.other;
  const statusBadge = STATUS_BADGES[expense.status] || STATUS_BADGES.pending;
  const [receiptUrl, setReceiptUrl] = useState(null);

  const viewReceipt = async () => {
    if (!expense.receipt_file_id) return;
    try {
      const res = await api.get(`/files/${expense.receipt_file_id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setReceiptUrl(url);
    } catch {
      toast.error('Cannot load receipt');
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 p-2 rounded-lg border bg-card text-sm">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium tabular-nums">${expense.amount.toFixed(2)}</span>
            <span className="text-muted-foreground text-xs truncate">{cat[lang]}</span>
            <Badge
              variant={statusBadge.variant}
              className={statusBadge.className || ''}
            >
              {statusBadge.label[lang]}
            </Badge>
          </div>
          {expense.description && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{expense.description}</p>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {expense.receipt_file_id && (
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={viewReceipt}>
              <ImageIcon className="h-3.5 w-3.5" />
            </Button>
          )}
          {canManage && expense.status === 'pending' && (
            <>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" onClick={onApprove}>
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={onReject}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          {isOwner && expense.status === 'pending' && (
            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={onDelete}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Receipt Viewer */}
      {receiptUrl && (
        <Dialog open={!!receiptUrl} onOpenChange={() => { URL.revokeObjectURL(receiptUrl); setReceiptUrl(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{lang === 'ru' ? 'Чек' : 'Receipt'}</DialogTitle>
            </DialogHeader>
            <img src={receiptUrl} alt="Receipt" className="w-full rounded-lg" />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function AddExpenseDialog({ open, onOpenChange, orderId, api, lang, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('cleaning_supplies');
  const [description, setDescription] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error(lang === 'ru' ? 'Укажите сумму' : 'Enter amount');
      return;
    }

    setSaving(true);
    try {
      // Create expense
      const res = await api.post('/expenses/', {
        expense_type: 'order',
        order_id: orderId,
        amount: parseFloat(amount),
        category,
        description: description || undefined,
      });

      const expenseId = res.data.id;

      // Upload receipt if selected
      if (receiptFile && expenseId) {
        const formData = new FormData();
        formData.append('file', receiptFile);
        await api.post(`/expenses/${expenseId}/receipt`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      toast.success(lang === 'ru' ? 'Расход добавлен' : 'Expense added');
      onOpenChange(false);
      setAmount('');
      setCategory('cleaning_supplies');
      setDescription('');
      setReceiptFile(null);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creating expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{lang === 'ru' ? 'Добавить расход' : 'Add Expense'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Amount */}
          <div>
            <label className="text-sm font-medium mb-1 block">
              {lang === 'ru' ? 'Сумма ($)' : 'Amount ($)'}
            </label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
            />
          </div>

          {/* Category */}
          <div>
            <label className="text-sm font-medium mb-1 block">
              {lang === 'ru' ? 'Категория' : 'Category'}
            </label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EXPENSE_CATEGORIES).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label[lang]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium mb-1 block">
              {lang === 'ru' ? 'Описание' : 'Description'}
            </label>
            <Input
              placeholder={lang === 'ru' ? 'Что было куплено...' : 'What was purchased...'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Receipt Photo */}
          <div>
            <label className="text-sm font-medium mb-1 block">
              {lang === 'ru' ? 'Фото чека' : 'Receipt photo'}
            </label>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => document.getElementById('receipt-input').click()}
              >
                <Camera className="h-4 w-4 mr-2" />
                {receiptFile
                  ? receiptFile.name
                  : (lang === 'ru' ? 'Сфотографировать чек' : 'Take receipt photo')
                }
              </Button>
              {receiptFile && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setReceiptFile(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <input
              id="receipt-input"
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {lang === 'ru' ? 'Отмена' : 'Cancel'}
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {lang === 'ru' ? 'Добавить' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
